import { useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RefreshCw, Activity, Cpu, Users, Building2, DollarSign, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import {
  useGetUsageSummaryQuery,
  useGetUsageByModelQuery,
  useGetUsageByOfficeQuery,
  useGetUsageByUserQuery,
  useGetSpendByOfficeQuery,
} from "@/redux/api/usageAnalytics.rtkq";

const PROVIDER_COLORS: Record<string, string> = {
  openai: "bg-green-600",
  anthropic: "bg-orange-600",
  gemini: "bg-blue-600",
  groq: "bg-purple-600",
};

type SortDirection = "asc" | "desc";
type SortState<K extends string> = { column: K; direction: SortDirection } | null;

const cycleSort = <K extends string>(prev: SortState<K>, column: K): SortState<K> => {
  if (!prev || prev.column !== column) return { column, direction: "desc" };
  if (prev.direction === "desc") return { column, direction: "asc" };
  return null;
};

const compareValues = (a: unknown, b: unknown, direction: SortDirection): number => {
  if (a == null && b == null) return 0;
  if (a == null) return direction === "asc" ? -1 : 1;
  if (b == null) return direction === "asc" ? 1 : -1;
  if (typeof a === "number" && typeof b === "number") {
    return direction === "asc" ? a - b : b - a;
  }
  const cmp = String(a).localeCompare(String(b), "he");
  return direction === "asc" ? cmp : -cmp;
};

const sortRows = <T, K extends keyof T & string>(rows: T[], sort: SortState<K>): T[] => {
  if (!sort) return rows;
  const { column, direction } = sort;
  return [...rows].sort((a, b) => compareValues(a[column], b[column], direction));
};

type SortableHeaderProps<K extends string> = {
  column: K;
  sort: SortState<K>;
  onSort: (column: K) => void;
  children: React.ReactNode;
};

const SortableHeader = <K extends string>({ column, sort, onSort, children }: SortableHeaderProps<K>) => {
  const isActive = sort?.column === column;
  const Icon = !isActive ? ArrowUpDown : sort.direction === "asc" ? ArrowUp : ArrowDown;
  return (
    <TableHead
      className="text-right cursor-pointer select-none hover:bg-muted/50"
      onClick={() => onSort(column)}
    >
      <div className="flex items-center gap-1 justify-end">
        <span>{children}</span>
        <Icon className={`h-3 w-3 ${isActive ? "opacity-100" : "opacity-40"}`} />
      </div>
    </TableHead>
  );
};

type ModelSortKey = "providerName" | "modelName" | "requests" | "inputTokens" | "outputTokens" | "costUsd" | "percentage";
type OfficeSortKey = "officeName" | "requests" | "inputTokens" | "outputTokens" | "credits" | "costUsd";
type UserSortKey = "displayName" | "email" | "requests" | "inputTokens" | "outputTokens" | "costUsd";

const UsageAnalytics = () => {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const dateRange = { from: dateFrom || undefined, to: dateTo || undefined };

  const { data: summary, isLoading: summaryLoading, refetch: refetchSummary } = useGetUsageSummaryQuery(dateRange);
  const { data: modelUsage = [], refetch: refetchModels } = useGetUsageByModelQuery(dateRange);
  const { data: officeUsage = [], refetch: refetchOffices } = useGetUsageByOfficeQuery(dateRange);
  const { data: userUsage = [], refetch: refetchUsers } = useGetUsageByUserQuery(dateRange);

  const [modelSort, setModelSort] = useState<SortState<ModelSortKey>>(null);
  const [officeSort, setOfficeSort] = useState<SortState<OfficeSortKey>>(null);
  const [userSort, setUserSort] = useState<SortState<UserSortKey>>(null);

  const sortedModels = useMemo(() => sortRows(modelUsage, modelSort), [modelUsage, modelSort]);
  const sortedOffices = useMemo(() => sortRows(officeUsage, officeSort), [officeUsage, officeSort]);
  const sortedUsers = useMemo(() => sortRows(userUsage, userSort), [userUsage, userSort]);

  const refetchAll = () => {
    refetchSummary();
    refetchModels();
    refetchOffices();
    refetchUsers();
  };

  const formatNumber = (n: number) => n?.toLocaleString() || "0";
  const formatCost = (n: number) => `$${(n || 0).toFixed(4)}`;

  return (
    <div className="space-y-6" dir="rtl">
      {/* Date range + refresh */}
      <div className="flex items-center gap-3 flex-wrap">
        <Input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          className="w-40"
          placeholder="מתאריך"
        />
        <Input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          className="w-40"
          placeholder="עד תאריך"
        />
        <Button variant="outline" size="sm" onClick={() => { setDateFrom(""); setDateTo(""); }}>
          נקה
        </Button>
        <Button variant="outline" size="sm" onClick={refetchAll}>
          <RefreshCw className="h-4 w-4 ml-1" />
          רענן
        </Button>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <Activity className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
              <div className="text-2xl font-bold">{formatNumber(summary.totalRequests)}</div>
              <div className="text-xs text-muted-foreground">בקשות</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Cpu className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
              <div className="text-2xl font-bold">{formatNumber(summary.totalInputTokens)}</div>
              <div className="text-xs text-muted-foreground">טוקנים נכנסים</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Cpu className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
              <div className="text-2xl font-bold">{formatNumber(summary.totalOutputTokens)}</div>
              <div className="text-xs text-muted-foreground">טוקנים יוצאים</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold">{formatNumber(summary.totalCredits)}</div>
              <div className="text-xs text-muted-foreground">קרדיטים</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Users className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
              <div className="text-2xl font-bold">{formatNumber(summary.activeUsers)}</div>
              <div className="text-xs text-muted-foreground">משתמשים פעילים</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Building2 className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
              <div className="text-2xl font-bold">{formatNumber(summary.activeOffices)}</div>
              <div className="text-xs text-muted-foreground">משרדים פעילים</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <DollarSign className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
              <div className="text-2xl font-bold">{formatCost(summary.totalCostUsd)}</div>
              <div className="text-xs text-muted-foreground">עלות משוערת</div>
            </CardContent>
          </Card>
        </div>
      )}

      {summaryLoading && <div className="text-center text-muted-foreground py-8">טוען נתונים...</div>}

      {/* LLM Provider/Model breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>שימוש לפי מודל LLM</CardTitle>
          <CardDescription>פילוח בקשות וטוקנים לפי ספק ומודל</CardDescription>
        </CardHeader>
        <CardContent>
          {modelUsage.length === 0 ? (
            <div className="text-center text-muted-foreground py-4">אין נתונים</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHeader column="providerName" sort={modelSort} onSort={(c) => setModelSort(cycleSort(modelSort, c))}>ספק</SortableHeader>
                  <SortableHeader column="modelName" sort={modelSort} onSort={(c) => setModelSort(cycleSort(modelSort, c))}>מודל</SortableHeader>
                  <SortableHeader column="requests" sort={modelSort} onSort={(c) => setModelSort(cycleSort(modelSort, c))}>בקשות</SortableHeader>
                  <SortableHeader column="inputTokens" sort={modelSort} onSort={(c) => setModelSort(cycleSort(modelSort, c))}>טוקנים נכנסים</SortableHeader>
                  <SortableHeader column="outputTokens" sort={modelSort} onSort={(c) => setModelSort(cycleSort(modelSort, c))}>טוקנים יוצאים</SortableHeader>
                  <SortableHeader column="costUsd" sort={modelSort} onSort={(c) => setModelSort(cycleSort(modelSort, c))}>עלות</SortableHeader>
                  <SortableHeader column="percentage" sort={modelSort} onSort={(c) => setModelSort(cycleSort(modelSort, c))}>אחוז</SortableHeader>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedModels.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Badge className={PROVIDER_COLORS[row.providerName] || "bg-gray-600"}>
                        {row.providerName}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{row.modelName}</TableCell>
                    <TableCell>{formatNumber(row.requests)}</TableCell>
                    <TableCell className="font-mono">{formatNumber(row.inputTokens)}</TableCell>
                    <TableCell className="font-mono">{formatNumber(row.outputTokens)}</TableCell>
                    <TableCell className="font-mono">{formatCost(row.costUsd)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-600 rounded-full"
                            style={{ width: `${row.percentage}%` }}
                          />
                        </div>
                        <span className="text-sm">{row.percentage}%</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Per-office usage */}
      <Card>
        <CardHeader>
          <CardTitle>שימוש לפי משרד</CardTitle>
        </CardHeader>
        <CardContent>
          {officeUsage.length === 0 ? (
            <div className="text-center text-muted-foreground py-4">אין נתונים</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHeader column="officeName" sort={officeSort} onSort={(c) => setOfficeSort(cycleSort(officeSort, c))}>משרד</SortableHeader>
                  <SortableHeader column="requests" sort={officeSort} onSort={(c) => setOfficeSort(cycleSort(officeSort, c))}>בקשות</SortableHeader>
                  <SortableHeader column="inputTokens" sort={officeSort} onSort={(c) => setOfficeSort(cycleSort(officeSort, c))}>טוקנים נכנסים</SortableHeader>
                  <SortableHeader column="outputTokens" sort={officeSort} onSort={(c) => setOfficeSort(cycleSort(officeSort, c))}>טוקנים יוצאים</SortableHeader>
                  <SortableHeader column="credits" sort={officeSort} onSort={(c) => setOfficeSort(cycleSort(officeSort, c))}>קרדיטים</SortableHeader>
                  <SortableHeader column="costUsd" sort={officeSort} onSort={(c) => setOfficeSort(cycleSort(officeSort, c))}>עלות</SortableHeader>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedOffices.map((row) => (
                  <TableRow key={row.officeId}>
                    <TableCell className="font-medium">{row.officeName || row.officeId}</TableCell>
                    <TableCell>{formatNumber(row.requests)}</TableCell>
                    <TableCell className="font-mono">{formatNumber(row.inputTokens)}</TableCell>
                    <TableCell className="font-mono">{formatNumber(row.outputTokens)}</TableCell>
                    <TableCell>{formatNumber(row.credits)}</TableCell>
                    <TableCell className="font-mono">{formatCost(row.costUsd)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Per-user usage */}
      <Card>
        <CardHeader>
          <CardTitle>שימוש לפי משתמש</CardTitle>
        </CardHeader>
        <CardContent>
          {userUsage.length === 0 ? (
            <div className="text-center text-muted-foreground py-4">אין נתונים</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHeader column="displayName" sort={userSort} onSort={(c) => setUserSort(cycleSort(userSort, c))}>שם</SortableHeader>
                  <SortableHeader column="email" sort={userSort} onSort={(c) => setUserSort(cycleSort(userSort, c))}>אימייל</SortableHeader>
                  <SortableHeader column="requests" sort={userSort} onSort={(c) => setUserSort(cycleSort(userSort, c))}>בקשות</SortableHeader>
                  <SortableHeader column="inputTokens" sort={userSort} onSort={(c) => setUserSort(cycleSort(userSort, c))}>טוקנים נכנסים</SortableHeader>
                  <SortableHeader column="outputTokens" sort={userSort} onSort={(c) => setUserSort(cycleSort(userSort, c))}>טוקנים יוצאים</SortableHeader>
                  <SortableHeader column="costUsd" sort={userSort} onSort={(c) => setUserSort(cycleSort(userSort, c))}>עלות</SortableHeader>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedUsers.map((row) => (
                  <TableRow key={row.userId}>
                    <TableCell className="font-medium">{row.displayName || "—"}</TableCell>
                    <TableCell className="text-sm">{row.email}</TableCell>
                    <TableCell>{formatNumber(row.requests)}</TableCell>
                    <TableCell className="font-mono">{formatNumber(row.inputTokens)}</TableCell>
                    <TableCell className="font-mono">{formatNumber(row.outputTokens)}</TableCell>
                    <TableCell className="font-mono">{formatCost(row.costUsd)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      {/* Budget Usage by Office */}
      <BudgetUsageCard />
    </div>
  );
};

function BudgetUsageCard() {
  const { data: spendData, isLoading } = useGetSpendByOfficeQuery();

  if (isLoading) return null;
  if (!spendData?.length) return null;

  const officesWithBudget = spendData.filter((o) => o.budgetUsd && o.budgetUsd > 0);
  if (officesWithBudget.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <DollarSign className="h-5 w-5" />
          תקציב חודשי לפי משרד
        </CardTitle>
        <CardDescription>אחוז שימוש מתוך התקציב החודשי ($)</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {officesWithBudget.map((office) => {
            const pct = office.percentage ?? 0;
            const color = pct > 90 ? "bg-red-500" : pct > 70 ? "bg-yellow-500" : "bg-green-500";
            return (
              <div key={office.officeId} className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="font-medium">{office.officeName}</span>
                  <span className="text-muted-foreground">
                    ${office.spentUsd.toFixed(2)} / ${office.budgetUsd!.toFixed(2)} ({pct.toFixed(1)}%)
                  </span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${color}`}
                    style={{ width: `${Math.min(pct, 100)}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

export default UsageAnalytics;
